import { DecimalPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { combineLatest, distinctUntilChanged, filter, firstValueFrom, switchMap, tap } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { SettingsService } from 'src/app/pages/settings/settings.service';
import { UserAssetsService } from 'src/app/pages/user-asset/user-assets.service';
import { DepositApi } from 'src/app/shared/apis/deposit.api';
import { WalletApi } from 'src/app/shared/apis/wallet.api';
import { CSButtonComponent } from 'src/app/shared/components/cs-button/cs-button.component';
import { CSInputComponent } from 'src/app/shared/components/cs-input/cs-input.component';
import { CSSelectComponent, ICSOption } from 'src/app/shared/components/cs-select/cs-select.component';
import { CustomizeFormModule } from 'src/app/shared/components/customize-form/customize-form.module';
import { ThirdAuthM8Component } from 'src/app/shared/components/third-auth-m8/third-auth-m8.component';
import { ThirdAuthM8Service } from 'src/app/shared/components/third-auth-m8/third-auth-m8.service';
import { InputFloatDirective } from 'src/app/shared/directive/input.directive';
import { ThirdAuthUser, Web3WalletType } from 'src/app/shared/interfaces/auth.interface';
import { NetworksInterface, TokenNetworksInterface } from 'src/app/shared/interfaces/deposit.interface';
import { CurrencyBalance, ForbidPaymentCategory, ForbidWithdrawType } from 'src/app/shared/interfaces/wallet.interface';
import { PipesModule } from 'src/app/shared/pipes/pipes.module';
import { Web3WalletNamePipe } from 'src/app/shared/pipes/web3-wallet-name.pipe';
import { Web3WalletService } from 'src/app/shared/service/web3-wallets/web3-wallet.service';
import { WalletDialogType, WalletService } from '../../wallet.service';
import { DepositWithdrawForbidComponent } from '../deposit-withdraw-forbid/deposit-withdraw-forbid.component';

@UntilDestroy()
@Component({
  selector: 'app-withdraw-web3-wallet',
  standalone: true,
  imports: [
    DecimalPipe,
    CSButtonComponent,
    ThirdAuthM8Component,
    CustomizeFormModule,
    PipesModule,
    CSInputComponent,
    CSSelectComponent,
    InputFloatDirective,
    Web3WalletNamePipe,
    DepositWithdrawForbidComponent,
  ],
  templateUrl: './withdraw-web3-wallet.component.html',
  styleUrl: './withdraw-web3-wallet.component.scss',
})
export class WithdrawWeb3WalletComponent implements OnInit {
  constructor(
    public walletService: WalletService,
    public thirdAuthM8Service: ThirdAuthM8Service,
    private appService: AppService,
    private web3WalletService: Web3WalletService,
    private depositApi: DepositApi,
    private walletApi: WalletApi,
    private userAssetsService: UserAssetsService,
    private settingsService: SettingsService,
    private deviceService: DeviceDetectorService,
  ) {}
  ThirdAuthUser = ThirdAuthUser;

  isConnected: boolean = false;
  isLoading: boolean = false;
  isWeb3WalletLoading: boolean = false;
  hasNetworkError: boolean = false;
  isTgApp: boolean = sessionStorage.getItem('isTgApp') === 'true'; // Only show TonConnect when using TG Mini App

  // 提款禁止加載狀態
  isForbidLoading: boolean = false;
  isWithdrawForbid: boolean = false;

  tokenNetworkList: TokenNetworksInterface[] = [];
  selectedCurrencyOption!: TokenNetworksInterface;
  currencyOptions: ICSOption<TokenNetworksInterface>[] = [];
  selectedNetworkOption: NetworksInterface | undefined;
  networkOptions: ICSOption<NetworksInterface>[] = [];
  userBalanceList: CurrencyBalance[] = [];

  amount: string = '';
  withdrawMaxAmount: string = '0';
  withdrawAddress: string = '';
  currentCurrency: string = 'USDT';
  currentDecimals: number = 6; // Default decimals for USDT
  currentBalance: number = 0;
  currentWeb3Wallet: Web3WalletType = Web3WalletType.MetaMask; // Default to MetaMask
  currentWeb3WalletBalance: string = '0';
  isHideWeb3Currency: boolean = false;

  /** 幣種提款限制 */
  coinLimitData: any;
  coinLimitLoading: boolean = false;

  /** 手續費類別 */
  wdFeeType: 'A' | 'C' = 'C';
  /** 手續費C的上限 */
  typeCFeeMax: number = 0;
  /** 手續費C的值*/
  fee: number = 0;
  /** 提款金額輸入驗證 */
  amountValid: boolean = true;
  /** 提款金額錯誤提示文字 */
  errText: string = '';

  async ngOnInit() {
    // 提款前先判断是否禁止提款
    await this.checkWithdrawForbid();

    this.isLoading = true;
    this.web3WalletService.isConnected$
      .pipe(
        tap(isConnected => (this.isConnected = isConnected)),
        filter(isConnected => isConnected), // Only proceed if connected
        switchMap(() =>
          combineLatest([
            this.appService.userBalance$,
            this.appService.currentCurrency$,
            this.depositApi.getTokenNetworks({ category: 'Withdraw' }),
            this.web3WalletService.walletType$,
            this.web3WalletService.accounts$,
            this.appService.sysConfig$,
          ]),
        ),
        distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
        untilDestroyed(this),
      )
      .subscribe(([userBalanceData, currentCurrencyData, tokenNetworksData, walletType, accountsData, sysConfig]) => {
        this.currentCurrency = currentCurrencyData?.currency || 'USDT'; // default to USDT

        // Init user balance list data and set current balance
        this.userBalanceList = userBalanceData || [];
        this.setCurrentBalance(this.currentCurrency);
        // Init token networks
        this.initTokenNetworks(tokenNetworksData?.data || []);
        // Init withdraw address (TON 提款需要使用「使用者友好地址」)
        const isTon = walletType === Web3WalletType.TonConnect;
        this.withdrawAddress = (isTon ? accountsData[1] : accountsData[0]) || '';
        // Init coinLimit
        this.getCoinLimit();
        this.wdFeeType = sysConfig?.handlingFeeType ?? 'C';
        this.isLoading = false;
      });

    this.web3WalletService.walletType$.subscribe(walletType => {
      if (walletType) {
        this.currentWeb3Wallet = walletType;

        // Hide web3 wallet balance for Coinbase Wallet on mobile/tablet devices
        const isMobileDevice =
          this.deviceService.isMobile() ||
          this.deviceService.isTablet() ||
          navigator.userAgent.match(/Android/i) !== null ||
          navigator.userAgent.match(/iPhone|iPad/i) !== null;
        this.isHideWeb3Currency = walletType === Web3WalletType.CoinbaseWallet && isMobileDevice;
      }
    });
  }

  initTokenNetworks(list: TokenNetworksInterface[]) {
    this.tokenNetworkList = list;
    this.currencyOptions = list?.map((item: TokenNetworksInterface) => ({
      label: item.name,
      value: item,
      img: item.icon,
    }));

    // Set default option
    if (this.currencyOptions.length > 0) {
      this.selectedCurrencyOption = list.find(item => item.currency === this.currentCurrency) || list[0];

      this.currentCurrency = this.selectedCurrencyOption.currency;
      this.networkOptions = this.selectedCurrencyOption.networks.map(network => ({
        label: network.desc,
        value: network,
      }));

      this.initNetworkSelect();
    }
  }

  initNetworkSelect() {
    if (this.networkOptions.length > 0) {
      // Set default network option
      if (this.currentWeb3Wallet === Web3WalletType.TonConnect) {
        this.selectedNetworkOption =
          this.networkOptions.find(item => item.value.network === 'TON')?.value || this.networkOptions[0].value;
      } else {
        this.selectedNetworkOption =
          this.networkOptions.find(item => item.value.network === 'ERC20')?.value || this.networkOptions[0].value;
      }

      this.onNetworkSelectChange();
    }
  }

  setCurrentBalance(curCurrency: string) {
    // Set current balance based on selected currency
    this.currentBalance = this.userBalanceList?.find(item => item.currency === curCurrency)?.balance || 0;
  }

  onCurrencySelectChange() {
    if (this.selectedCurrencyOption) {
      this.resetAmount();
      this.currentCurrency = this.selectedCurrencyOption.currency;
      this.setCurrentBalance(this.currentCurrency);

      // Reset network options based on selected currency
      this.networkOptions = [];
      this.selectedNetworkOption = undefined;

      this.networkOptions =
        this.tokenNetworkList
          .find(item => item.currency === this.currentCurrency)
          ?.networks.map(network => ({
            label: network.desc,
            value: network,
          })) || [];

      this.initNetworkSelect();
      this.getCoinLimit();
    }
  }

  async onNetworkSelectChange() {
    // Reset state before switching network
    this.hasNetworkError = false;

    if (this.selectedNetworkOption) {
      this.resetAmount();

      const { chainId, decimals } = this.selectedNetworkOption;
      // Default to 6 decimals if not specified
      this.currentDecimals = decimals || 6;

      // Switch network using Coinbase Wallet service
      await this.web3WalletService.switchNetwork(chainId).catch(() => {
        this.resetAmount();
        this.hasNetworkError = true;

        // 當 Network 不是預設第一個的時候，才切回第一個網路
        if (this.networkOptions.findIndex(item => item.value.chainId === chainId) !== 0) {
          this.initNetworkSelect();
        }
      });

      if (!this.hasNetworkError) {
        // Fetch web3 wallet balance after network switch
        await this.fetchWeb3WalletBalance();
      }
    }
  }

  async fetchWeb3WalletBalance() {
    if (this.isConnected && this.selectedCurrencyOption && this.selectedNetworkOption) {
      this.isWeb3WalletLoading = true;

      const { contractAddress } = this.selectedNetworkOption;

      // Clear previous current web3 wallet balance
      this.currentWeb3WalletBalance = '0';

      // Fetch balance and check if contract address
      if (contractAddress) {
        this.currentWeb3WalletBalance =
          (await this.web3WalletService.fetchContractBalance(this.selectedNetworkOption)) || '0';
      } else {
        // Handle non-contract address data
        this.currentWeb3WalletBalance = (await this.web3WalletService.fetchBalance()) || '0';
      }

      // Format current web3 wallet balance to string, convert '0.0' to '0'
      this.currentWeb3WalletBalance = parseFloat(this.currentWeb3WalletBalance).toString();

      this.isWeb3WalletLoading = false;
    }
  }

  fetchWithdrawMaxAmount() {
    if (this.isConnected && this.selectedCurrencyOption && this.selectedNetworkOption) {
      // Clear previous max amount
      this.withdrawMaxAmount = '0';

      // Get web3 wallet balance as max amount
      this.withdrawMaxAmount = this.currentBalance.toString();

      const maxAmount = this.coinLimitData?.availQuota || 0;
      if (maxAmount < Number(this.withdrawMaxAmount)) {
        this.withdrawMaxAmount = maxAmount.toString();
      }
    }
  }

  onSetWithdrawMaxAmount() {
    this.amount = this.withdrawMaxAmount;
  }

  getWithdrawMaxAmount() {
    return Number(this.withdrawMaxAmount);
  }

  resetAmount() {
    this.amount = '';
    this.currentWeb3WalletBalance = '0';
    this.currentDecimals = 6;
  }

  isAllLoading() {
    return this.isLoading;
  }

  isReadyToWithdraw() {
    if (this.isAllLoading()) {
      return true;
    }

    return (
      this.isConnected &&
      !this.hasNetworkError &&
      this.selectedCurrencyOption !== undefined &&
      this.selectedNetworkOption !== undefined &&
      this.withdrawAddress !== '' &&
      Number(this.amount) > 0 &&
      this.amountValid
    );
  }

  async getCoinLimit() {
    this.coinLimitLoading = true;
    this.coinLimitData = await this.walletService.getCoinLimit(this.currentCurrency);
    // Set withdraw max amount
    this.fetchWithdrawMaxAmount();
    this.updateTypeCFeeMax();
    this.coinLimitLoading = false;
  }

  async onWeb3Withdraw() {
    if (this.isConnected && this.selectedCurrencyOption.currency && this.selectedNetworkOption) {
      this.walletService.otpType = 'Withdraw';
      this.walletService.email = this.appService.userInfo$.getValue()?.email || '';
      this.walletService.preservedWithdrawState = {
        currentCurrency: this.selectedCurrencyOption.currency,
        selectedNetwork: this.selectedNetworkOption.network,
        address: this.withdrawAddress,
        amount: Number(this.amount),
      };

      // Check if user has 2FA enabled
      if (this.settingsService.hasAny2FA()) {
        this.walletService.setWalletDialogType(WalletDialogType.VERIFY_2FA);
      } else {
        this.settingsService
          .openEnable2faDialog()
          .afterClosed()
          .subscribe(() => this.walletService.closeWallet());
      }
    }
  }

  /**
   * 轉換手續費C的上限為當前幣種
   */
  updateTypeCFeeMax() {
    if (this.wdFeeType !== 'C') return;
    const allRate = this.userAssetsService.allRate.value;
    if (allRate && this.currentCurrency) {
      const currFiatRate = allRate.rates.find((rate: any) => rate.currency === this.currentCurrency);
      this.typeCFeeMax = (this.coinLimitData?.perFeeLimit || 0).divide(currFiatRate?.rate || 1);
    }
  }

  /**
   * 設置實際當賬金額
   * 包含扣除網路手續費與平台手續費
   */
  setTypeCFee() {
    if (isNaN(Number(this.amount))) {
      return;
    }
    // 有免費提款次數
    if (this.coinLimitData.withdrawCount > 0) {
      this.fee = 0;
    } else {
      // 没有免费提款次数，要用百分比去计算手续费
      this.fee = Number(this.amount).subtract(this.coinLimitData.withdrawalFeeProportion).divide(100);
      // 如果百分比手续费大于最大手续费，则使用最大手续费
      if (this.fee > this.typeCFeeMax) {
        this.fee = this.typeCFeeMax;
      }
    }
  }

  /**
   * 金額輸入的綁定
   */
  onAmountInput() {
    // 完全不輸入金額時也不要顯示錯誤訊息
    if (this.amount === '') {
      this.amountValid = true;
      this.errText = '';
      this.fee = 0;
      return;
    }

    if (this.coinLimitData?.availQuota > 0) {
      this.setTypeCFee();
    }

    if (!this.selectedNetworkOption) return;

    // 優先檢查 withdrawQuota 是否 > 0, from ticket-11276
    // *Validation order: BE must evaluate WDR first, balance second.
    if (this.coinLimitData.withdrawQuota > 0) {
      this.errText = 'You need to complete the required turnover before making a withdrawal.';
      this.amountValid = false;
      // 檢查輸入金額是否小於單筆最小提款金額
    } else if (Number(this.amount) < this.selectedNetworkOption?.minAmount) {
      this.errText = 'The withdrawal amount is below the minimum limit';
      this.amountValid = false;
      // 檢查輸入金額是否大於可用提款額度
    } else if (Number(this.amount) > this.coinLimitData?.availQuota) {
      this.errText = 'The withdrawal amount exceeds your available balance';
      this.amountValid = false;
      // 檢查輸入金額是否大於單筆最大提款金額
    } else if (Number(this.amount) > this.selectedNetworkOption?.maxAmount) {
      this.errText = 'The withdrawal amount exceeds the maximum limit';
      this.amountValid = false;
      // 金額輸入正確，清除錯誤訊息
    } else {
      this.amountValid = true;
      this.errText = '';
    }
  }

  /**
   * 提款前先判断是否禁止提款
   */
  async checkWithdrawForbid() {
    this.isForbidLoading = true;
    this.isWithdrawForbid = await await firstValueFrom(
      this.walletApi.forbidPaymentInfo({
        category: ForbidPaymentCategory.Withdraw,
        withdrawType: ForbidWithdrawType.Encryption,
      }),
    );
    this.isForbidLoading = false;
  }
}
